import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { ThemeButton } from '@shared/ui/buttons';
import { useForm, useWatch } from 'react-hook-form';
import { useEffect } from 'react';
import { CardsPreviewGroup } from '@shared/ui/parts';
import { useTranslation } from 'react-i18next';
import { cardStatuses } from '@utils/statuses';
import { useCards } from '@app/api/cards/useCards';
import { useSelector, useDispatch } from 'react-redux';
import { formatBalanceUi, FRACTION_DIGITS } from '@utils/balance-helpers';
import { setSelected } from '@app/store/slices/cardsSlice';
import { AmountInput } from '@shared/ui/forms/inputs';
import {
  validateTransactionLimit,
  validateServerErrors,
} from '@utils/validation';

export default function ChangeLimitsCardDialog({ close, data }) {
  const { t } = useTranslation();
  const { changeLimits } = useCards();
  const { tariff } = useSelector((state) => state.tariff);

  const dispatch = useDispatch();
  const availableCards = data.filter(
    (item) => cardStatuses[item?.status] !== 'blocked',
  );

  const {
    control,
    handleSubmit,
    trigger,
    setError,
    formState: { errors, isSubmitting },
  } = useForm();

  const transactionLimitWatch = useWatch({
    control,
    name: 'transaction_limit',
  });
  const monthlyBalanceLimitWatch = useWatch({
    control,
    name: 'monthly_balance_limit',
  });

  useEffect(() => {
    if (transactionLimitWatch != null && monthlyBalanceLimitWatch != null) {
      trigger('transaction_limit');
    }
  }, [monthlyBalanceLimitWatch, transactionLimitWatch]);

  async function onSubmit(formData) {
    const payload = {
      card_ids: availableCards.map((x) => x.id),
      transaction_limit: Number(formData.transaction_limit),
      monthly_balance_limit: Number(formData.monthly_balance_limit),
    };
    try {
      const res = await changeLimits(payload);
      if (res) {
        dispatch(setSelected([]));
        close();
      }
    } catch (error) {
      validateServerErrors(setError, error);
    }
  }

  return (
    <>
      <DialogContent>
        <div className="form">
          <div className="formGroup-grid">
            <AmountInput
              integer
              isRequired={true}
              control={control}
              name="transaction_limit"
              label={t('transaction_limit')}
              placeholder={`${t('max')} ${formatBalanceUi(tariff?.transaction_limit, null, FRACTION_DIGITS.INTEGER)}`}
              max={tariff?.transaction_limit}
              currency="USD"
              hint={`${t('max')} ${formatBalanceUi(tariff?.transaction_limit, 'USD', FRACTION_DIGITS.INTEGER)}`}
              error={errors.transaction_limit}
              validation={(value) =>
                validateTransactionLimit(value, monthlyBalanceLimitWatch)
              }
            />
            <AmountInput
              integer
              isRequired={true}
              control={control}
              name="monthly_balance_limit"
              label={t('card_limit')}
              placeholder={`${t('max')} ${formatBalanceUi(tariff?.monthly_card_spent_limit, null, FRACTION_DIGITS.INTEGER)}`}
              max={tariff?.monthly_card_spent_limit}
              currency="USD"
              hint={`${t('max')} ${formatBalanceUi(tariff?.monthly_card_spent_limit, 'USD', FRACTION_DIGITS.INTEGER)}`}
              error={errors.monthly_balance_limit}
            />
          </div>
        </div>
        <br />
        <CardsPreviewGroup data={availableCards} />
      </DialogContent>
      <DialogActions>
        <ThemeButton
          minWidth
          loading={isSubmitting}
          startIcon="check"
          block={false}
          onClick={handleSubmit(onSubmit)}
        >
          {t('save')}
        </ThemeButton>
      </DialogActions>
    </>
  );
}
